import { asPlaybookId } from "@repo/app-types/entities/playbook";
import type { ReportTableData } from "@repo/app-types/entities/report";
import { Size } from "@repo/ui/helpers/helpers";
import { Link } from "@repo/ui/Link/routerLink";
import { getPillColor, Pill } from "@repo/ui/Pills/pill";
import { TableSkeletonContent } from "@repo/ui/Skeletons/skeletons";
import {
  IconArrowDown,
  IconArrowUp,
  IconCornerDownRight,
} from "@tabler/icons-react";
import { type FC, type ReactNode, useId, useMemo, useState } from "react";
import {
  Cell,
  Column,
  Row,
  type SortDescriptor,
  Table,
  TableBody,
  TableHeader,
} from "react-aria-components";
import { useParams } from "react-router";
import { Area, AreaChart, ReferenceLine, YAxis } from "recharts";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../../app/appPaths.js";
import { BaseCard } from "./baseCard.js";
import { SparklineGradient } from "./sparklineGradient.js";
import { computeValueChange, renderValueAsWidget } from "./values.js";

export interface TableCardProps {
  title: ReactNode;
  data?: ReportTableData;
}

export const TableCard: FC<TableCardProps> = (props) => {
  const { data } = props;
  const labelId = useId();

  return (
    <BaseCard title={props.title} labelId={labelId}>
      {!data ? (
        <TableSkeletonContent />
      ) : !data.columns.length || !data.rows.length ? (
        <div className="flex flex-auto items-center justify-center py-12 text-center text-sm text-gray-400">
          No data
        </div>
      ) : (
        <TableCardContent labelId={labelId} data={data} />
      )}
    </BaseCard>
  );
};

interface TableCardColumnDefinition {
  id: string;
  className?: string;
  isRowHeader?: boolean;
  header: ReactNode;
  isTabular?: boolean;
  allowsSorting?: boolean;
  getSortValue?: (
    row: ReportTableData["rows"][number],
  ) => string | number | null;
  renderCell: (row: ReportTableData["rows"][number]) => ReactNode;
}

const TableCardContent: FC<{ labelId: string; data: ReportTableData }> = ({
  labelId,
  data,
}) => {
  const { playbookId: playbookIdParam } = useParams();
  const playbookId = playbookIdParam ? asPlaybookId(playbookIdParam) : null;

  const sortable = data.sortable !== false;

  const columns = useMemo((): TableCardColumnDefinition[] => {
    return data.columns.flatMap(
      (column, index): TableCardColumnDefinition[] => {
        const columnRange = computeColumnNumericRange(data.rows, index);
        const getValueColor = (value: number): string | undefined =>
          renderColumnValueColor(value, columnRange, column.indicatorType);
        switch (column.valueType) {
          case "text":
            return [
              {
                id: `column_${index}`,
                header: column.label,
                isRowHeader: index === 0,
                allowsSorting: sortable,
                getSortValue: (row) => {
                  const cell = row.cells[index];
                  return typeof cell?.value === "string" ? cell.value : null;
                },
                renderCell: (row) => {
                  const cell = row.cells[index];
                  if (cell === undefined) return null;
                  const teammateHref =
                    playbookId && cell.teammateId
                      ? appPaths.teammate.build({
                          playbookId,
                          teammateId: cell.teammateId,
                        })
                      : null;
                  const indentLevel = cell.indentLevel ?? 0;
                  return (
                    <div className="flex items-center gap-2">
                      {indentLevel > 0 && (
                        <IconCornerDownRight
                          className="size-4 shrink-0 text-gray-400"
                          style={{
                            marginLeft: `${2 + Math.max(0, indentLevel - 1) * 24}px`,
                            marginRight: "-2px",
                          }}
                        />
                      )}
                      <div className="w-48 max-w-fit flex-auto truncate">
                        {teammateHref ? (
                          <Link
                            href={teammateHref}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-blue-600 hover:text-blue-500"
                          >
                            {cell.value}
                          </Link>
                        ) : (
                          cell.value
                        )}
                      </div>
                    </div>
                  );
                },
              },
            ];

          case "pill":
            return [
              {
                id: `column_${index}`,
                className: "w-0",
                header: column.label,
                allowsSorting: sortable,
                getSortValue: (row) => {
                  const value = row.cells[index]?.value;
                  return typeof value === "string" ? value : null;
                },
                renderCell: (row) => {
                  const value = row.cells[index]?.value;

                  if (typeof value === "string") {
                    const option = column.pillOptions?.find(
                      (o) => o.value === value,
                    );
                    const label = option?.label ?? value;
                    const color = option?.color ?? "gray";

                    return (
                      <Pill
                        size={Size.SMALL}
                        color={getPillColor(color)}
                        label={label}
                      />
                    );
                  }

                  return null;
                },
              },
            ];

          case "passRate":
            return [
              {
                id: `column_${index}`,
                className: "w-0",
                header: column.label,
                isTabular: true,
                allowsSorting: sortable,
                getSortValue: (row) => {
                  const value = row.cells[index]?.value;
                  return typeof value === "number" ? value : null;
                },
                renderCell: (row) => {
                  const value = row.cells[index]?.value;

                  if (typeof value === "number" || value === null) {
                    const color =
                      typeof value === "number"
                        ? getValueColor(value)
                        : undefined;
                    return (
                      <div
                        className={twMerge(
                          "text-right whitespace-nowrap tabular-nums",
                          color && "font-medium",
                        )}
                        style={color ? { color } : undefined}
                      >
                        {renderValueAsWidget({
                          value,
                          valueType: "passRate",
                        })}
                      </div>
                    );
                  }

                  return null;
                },
              },
              ...(column.hasSparkline
                ? [
                    {
                      id: `column_${index}_sparkline`,
                      className: "w-0",
                      header: "Trend",
                      renderCell: (row) => {
                        const cell = row.cells[index];
                        if (cell === undefined) return null;
                        const { value, previousValue, sparkline } = cell;

                        if (
                          (typeof value === "number" || value === null) &&
                          sparkline
                        ) {
                          return (
                            <Sparkline
                              value={value}
                              previousValue={previousValue ?? null}
                              sparkline={sparkline}
                              sparklineReferenceValue={
                                column.sparklineReferenceValue
                              }
                              valueType="passRate"
                              indicatorType={column.indicatorType ?? "neutral"}
                            />
                          );
                        }

                        return null;
                      },
                    } satisfies TableCardColumnDefinition,
                  ]
                : []),
              ...(column.hasPreviousValue
                ? [
                    {
                      id: `column_${index}_change`,
                      className: "w-0",
                      header: "Delta",
                      allowsSorting: sortable,
                      getSortValue: (row) => {
                        const cell = row.cells[index];
                        if (cell === undefined) return null;
                        const { value, previousValue } = cell;

                        if (
                          (typeof value === "number" || value === null) &&
                          (typeof previousValue === "number" ||
                            previousValue === null)
                        ) {
                          const valueChange = computeValueChange({
                            value,
                            previousValue,
                            valueType: "passRate",
                            indicatorType: column.indicatorType ?? "neutral",
                          });

                          return valueChange.difference;
                        }

                        return null;
                      },
                      renderCell: (row) => {
                        const cell = row.cells[index];
                        if (cell === undefined) return null;
                        const { value, previousValue } = cell;

                        if (
                          (typeof value === "number" || value === null) &&
                          (typeof previousValue === "number" ||
                            previousValue === null)
                        ) {
                          const valueChange = computeValueChange({
                            value,
                            previousValue,
                            valueType: "passRate",
                            indicatorType: column.indicatorType ?? "neutral",
                          });

                          return (
                            <div className="text-right whitespace-nowrap tabular-nums">
                              {valueChange.content}
                            </div>
                          );
                        }

                        return null;
                      },
                    } satisfies TableCardColumnDefinition,
                  ]
                : []),
            ];

          default: {
            const numericValueType = column.valueType;
            return [
              {
                id: `column_${index}`,
                className: "w-0",
                header: column.label,
                allowsSorting: sortable,
                getSortValue: (row) => {
                  const value = row.cells[index]?.value;
                  return typeof value === "number" ? value : null;
                },
                renderCell: (row) => {
                  const value = row.cells[index]?.value;

                  if (typeof value === "number" || value === null) {
                    const color =
                      typeof value === "number"
                        ? getValueColor(value)
                        : undefined;
                    return (
                      <div
                        className={twMerge(
                          "text-right whitespace-nowrap tabular-nums",
                          color && "font-medium",
                        )}
                        style={color ? { color } : undefined}
                      >
                        {renderValueAsWidget({
                          value,
                          valueType: numericValueType,
                        })}
                      </div>
                    );
                  }

                  return null;
                },
              },
              ...(column.hasSparkline
                ? [
                    {
                      id: `column_${index}_sparkline`,
                      className: "w-0",
                      header: "Trend",
                      renderCell: (row) => {
                        const cell = row.cells[index];
                        if (cell === undefined) return null;
                        const { value, previousValue, sparkline } = cell;

                        if (
                          (typeof value === "number" || value === null) &&
                          sparkline
                        ) {
                          return (
                            <Sparkline
                              value={value}
                              previousValue={previousValue ?? null}
                              sparkline={sparkline}
                              sparklineReferenceValue={
                                column.sparklineReferenceValue
                              }
                              valueType={numericValueType}
                              indicatorType={column.indicatorType ?? "neutral"}
                            />
                          );
                        }

                        return null;
                      },
                    } satisfies TableCardColumnDefinition,
                  ]
                : []),
              ...(column.hasPreviousValue
                ? [
                    {
                      id: `column_${index}_change`,
                      className: "w-0",
                      header: "Delta",
                      allowsSorting: sortable,
                      getSortValue: (row) => {
                        const cell = row.cells[index];
                        if (cell === undefined) return null;
                        const { value, previousValue } = cell;

                        if (
                          (typeof value === "number" || value === null) &&
                          (typeof previousValue === "number" ||
                            previousValue === null)
                        ) {
                          const valueChange = computeValueChange({
                            value,
                            previousValue,
                            valueType: numericValueType,
                            indicatorType: column.indicatorType ?? "neutral",
                          });

                          return valueChange.difference;
                        }

                        return null;
                      },
                      renderCell: (row) => {
                        const cell = row.cells[index];
                        if (cell === undefined) return null;
                        const { value, previousValue } = cell;

                        if (
                          (typeof value === "number" || value === null) &&
                          (typeof previousValue === "number" ||
                            previousValue === null)
                        ) {
                          const valueChange = computeValueChange({
                            value,
                            previousValue,
                            valueType: numericValueType,
                            indicatorType: column.indicatorType ?? "neutral",
                          });

                          return (
                            <div className="text-right whitespace-nowrap tabular-nums">
                              {valueChange.content}
                            </div>
                          );
                        }

                        return null;
                      },
                    } satisfies TableCardColumnDefinition,
                  ]
                : []),
            ];
          }
        }
      },
    );
  }, [data.columns, data.rows, playbookId, sortable]);

  const rows = useMemo(
    () =>
      data.rows.map((row, index) => ({
        ...row,
        id: `row_${index}`,
      })),
    [data.rows],
  );

  const [sortDescriptor, setSortDescriptor] = useState<
    SortDescriptor | undefined
  >(() => {
    const initialSortDescriptor = data.initialSortDescriptor;
    if (!initialSortDescriptor) {
      return undefined;
    }
    const initialColumn = columns[initialSortDescriptor.columnIndex];
    if (!initialColumn) {
      return undefined;
    }
    return {
      column: initialColumn.id,
      direction: initialSortDescriptor.ascending ? "ascending" : "descending",
    };
  });

  const sortedRows = useMemo(() => {
    if (!sortDescriptor) {
      return rows;
    }

    const column = columns.find((c) => c.id === sortDescriptor.column);
    const getSortValue = column?.getSortValue;
    if (!getSortValue) {
      return rows;
    }

    return rows.toSorted((a, b) => {
      let result: number;

      const aVal = getSortValue(a);
      const bVal = getSortValue(b);

      if (typeof aVal === "string" || typeof bVal === "string") {
        const aStr = typeof aVal === "string" ? aVal : "";
        const bStr = typeof bVal === "string" ? bVal : "";

        result = aStr.localeCompare(bStr);
      } else if (typeof aVal === "number" || typeof bVal === "number") {
        const aNum = typeof aVal === "number" ? aVal : 0;
        const bNum = typeof bVal === "number" ? bVal : 0;

        result = aNum - bNum;
      } else {
        result = 0;
      }

      return sortDescriptor.direction === "descending" ? -result : result;
    });
  }, [rows, sortDescriptor, columns]);

  return (
    <Table
      aria-labelledby={labelId}
      className="w-full"
      sortDescriptor={sortDescriptor}
      onSortChange={setSortDescriptor}
    >
      <TableHeader columns={columns} className="border-b border-border-light">
        {(column) => (
          <Column
            id={column.id}
            isRowHeader={column.isRowHeader}
            allowsSorting={column.allowsSorting}
            className={twMerge(
              "pt-1.5 pr-6 pb-3 pl-0 text-left text-xs font-medium text-gray-400 last:pr-0 data-[allows-sorting]:cursor-pointer data-[allows-sorting]:hover:text-muted-foreground",
              column.className,
            )}
          >
            {({ sortDirection }) => (
              <div className="flex max-w-32 items-center gap-1">
                <span className="truncate">{column.header}</span>
                {sortDirection !== undefined ? (
                  sortDirection === "ascending" ? (
                    <IconArrowDown className="size-3 shrink-0 text-gray-400" />
                  ) : (
                    <IconArrowUp className="size-3 shrink-0 text-gray-400" />
                  )
                ) : null}
              </div>
            )}
          </Column>
        )}
      </TableHeader>
      <TableBody
        items={sortedRows}
        dependencies={[columns]}
        className="text-sm"
      >
        {(row) => (
          <Row columns={columns} className="border-b border-border-light">
            {(column) => (
              <Cell className="py-1.5 pr-6 pl-0 last:pr-0">
                {column.renderCell(row)}
              </Cell>
            )}
          </Row>
        )}
      </TableBody>
    </Table>
  );
};

const Sparkline: FC<{
  value: number | null;
  previousValue: number | null;
  sparkline: readonly (number | null)[];
  sparklineReferenceValue?: number | null;
  valueType:
    | "passRate"
    | "percentage"
    | "calls"
    | "teammates"
    | "gradings"
    | "weeks"
    | "oneToFiveScoreAverage"
    | "usd"
    | "years";
  indicatorType: "neutral" | "positive" | "negative";
}> = (props) => {
  const gradientId = useId();
  const valueChange = computeValueChange(props);

  return (
    <div className="-m-[5px]">
      <AreaChart
        style={{
          width: "100px",
          height: "40px",
        }}
        data={props.sparkline.map((value) => ({ value }))}
      >
        <SparklineGradient
          id={gradientId}
          color={valueChange.indication.sparklineColor}
        />
        <YAxis hide domain={["auto", "auto"]} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={valueChange.indication.sparklineColor}
          strokeWidth={1.5}
          fill={`url(#${gradientId})`}
        />

        {props.sparklineReferenceValue !== undefined &&
        props.sparklineReferenceValue !== null ? (
          <ReferenceLine
            y={props.sparklineReferenceValue}
            stroke="var(--color-gray-400)"
            strokeDasharray="3 3"
          />
        ) : null}
      </AreaChart>
    </div>
  );
};

function computeColumnNumericRange(
  rows: ReportTableData["rows"],
  columnIndex: number,
): { min: number; max: number } | undefined {
  let min = Infinity;
  let max = -Infinity;
  for (const row of rows) {
    const value = row.cells[columnIndex]?.value;
    if (typeof value !== "number") continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return undefined;
  }
  return { min, max };
}

function renderColumnValueColor(
  value: number,
  range: { min: number; max: number } | undefined,
  indicatorType: "neutral" | "positive" | "negative" | undefined,
): string | undefined {
  if (!range || !indicatorType || indicatorType === "neutral") return undefined;
  const normalized = (value - range.min) / (range.max - range.min);
  const clamped = Math.max(0, Math.min(1, normalized));
  const ratio = indicatorType === "negative" ? 1 - clamped : clamped;
  // Interpolate the OKLCH hue from red (~27°) to green (~142°) at a fixed
  // lightness and chroma for a perceptually uniform red → orange → yellow →
  // lime → green ramp that stays readable as text on a light background.
  const hue = 27 + ratio * (142 - 27);
  return `oklch(63.7% 0.237 ${hue.toFixed(2)})`;
}
